import Service from '@ember/service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Store from '@ember-data/store';
import type CareerPathwaysApiService from 'tio-employee/services/career-pathways-api';
import type {
  EmployerContributions,
  StudentLoanSummary,
} from 'tio-employee/services/career-pathways-api';
import type SessionContextService from 'tio-employee/services/session-context';
import type EmployeeService from 'tio-employee/services/employee';
import type PartnerService from 'tio-employee/services/partner';
import type PersonModel from 'tio-common/models/person';
import type AccountModel from 'tio-common/models/account';
import type LoanModel from 'tio-common/models/loan';
import type TasApplicationModel from 'tio-common/models/tas-application';
import type TasProgramInstanceModel from 'tio-common/models/tas-program-instance';
import type PslfFormModel from 'tio-common/models/pslf-form';
import type PslfProfileModel from 'tio-common/models/pslf-profile';
import type { LoanAccount } from 'tio-employee/components/reimagined/types';
import { getCurrentDemoTasScenarioId } from 'tio-employee/utils/career-pathways-demo-data';

export type DataSourceType = 'live' | 'demo' | 'placeholder';

/**
 * Comprehensive tracking of data sources across all reimagined pages.
 * Each field tracks whether it comes from live backend data, demo data, or is a placeholder.
 *
 * DATA SOURCE GAPS (what needs backend work):
 * - careerCapitalScore: No backend API yet - needs career pathways service
 * - skills: No skills tracking in backend - needs career pathways service
 * - careerPlan: No career plan model in backend - needs career pathways service
 * - recommendations: No recommendation engine - needs AI service
 * - optimization: No AI optimizer - needs AI service to analyze best allocation
 */
export interface DataSourceInfo {
  // Employee/Profile Data
  employeeProfile: DataSourceType;
  employerName: DataSourceType;

  // Financial Data
  studentLoans: DataSourceType;
  employerContributions: DataSourceType;
  section127Allocation: DataSourceType;
  section127Used: DataSourceType;

  // Benefit Eligibility
  pslfEligibility: DataSourceType;
  tasEligibility: DataSourceType;

  // Career Data (all demo/placeholder until backend built)
  careerCapitalScore: DataSourceType;
  skills: DataSourceType;
  careerPlan: DataSourceType;
  recommendations: DataSourceType;

  // Optimizer (placeholder until AI built)
  optimization: DataSourceType;
}

/**
 * Live employee profile data extracted from session/models
 */
export interface LiveEmployeeProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  currentRole: {
    title: string;
    department: string;
    startDate: string;
    yearsInRole: number;
  };
  taBenefit: {
    annualMax: number;
    used: number;
    remaining: number;
    eligiblePrograms: number;
  };
  slrBenefit: {
    monthlyContribution: number;
    annualMax: number;
    yearToDate: number;
    lifetime: number;
  };
  pslfEligible: boolean;
  pslfPaymentCount: number;
}

/**
 * Live Section 127 allocation derived from plan data
 */
export interface LiveSection127Info {
  annualLimit: number;
  yearToDateUsed: number;
  remaining: number;
  loanAllocation: number;
  tuitionAllocation: number;
  isLiveData: boolean;
}

/**
 * Live PSLF data from pslf-profile and pslf-form models
 */
export interface LivePslfData {
  isEligible: boolean;
  isEnrolled: boolean;
  qualifyingPayments: number;
  estimatedForgiveness: number;
  forgivenessDate: string | null;
  currentEmployer: {
    name: string;
    isQualifying: boolean;
    startDate: string | null;
  } | null;
  forms: LivePslfForm[];
  recentActivity: {
    type: 'form_submitted' | 'form_signed' | 'payment_certified' | 'status_change';
    date: string;
    description: string;
  }[];
  isLiveData: boolean;
}

export interface LivePslfForm {
  id: string;
  status: string;
  formType: string;
  employerName: string;
  employmentStartDate: string | null;
  employmentEndDate: string | null;
  borrowerSignedAt: string | null;
  employerSignedAt: string | null;
  submittedAt: string | null;
  createdAt: string;
}

/**
 * Live TAS (Tuition Assistance) data
 */
export interface LiveTasData {
  isEligible: boolean;
  participantState: string | null;
  programs: LiveTasProgram[];
  applications: LiveTasApplication[];
  summary: {
    totalApproved: number;
    totalPaid: number;
    pendingCount: number;
    activeCount: number;
    completedCount: number;
  };
  isLiveData: boolean;
}

export interface LiveTasProgram {
  id: string;
  name: string;
  state: string;
  institution: string | null;
  major: string | null;
  startDate: string | null;
}

export interface LiveTasApplication {
  id: string;
  name: string;
  state: string;
  paymentState: string;
  approvedTotal: number;
  paidTotal: number;
  coursesBeginDate: string | null;
  coursesEndDate: string | null;
  createdAt: string;
  programName: string | null;
}

export default class ReimaginedDataService extends Service {
  @service declare store: Store;
  @service declare careerPathwaysApi: CareerPathwaysApiService;
  @service declare sessionContext: SessionContextService;
  @service declare employee: EmployeeService;
  @service declare partner: PartnerService;

  /**
   * Tracked state for data audit mode. When enabled, data source badges are shown
   * and placeholder sections are highlighted with marching ants.
   */
  @tracked isDataAuditEnabled = this.initDataAuditState();

  /**
   * Tracked state for data source docs panel visibility
   */
  @tracked isDocsOpen = false;

  /**
   * The section ID to scroll to when opening docs
   */
  @tracked docsSectionId?: string;

  private initDataAuditState() {
    if (typeof window === 'undefined') {
      return false;
    }
    // Read from localStorage for persistence across page refreshes
    const stored = localStorage.getItem('reimagined-data-audit');
    const isEnabled = stored === 'true';
    // Apply the body class on init to match stored state
    if (isEnabled) {
      document.body.classList.add('reimagined-data-audit');
    }
    return isEnabled;
  }

  /**
   * Toggle data audit mode. This shows/hides data source badges and
   * highlights placeholder sections.
   */
  toggleDataAudit = () => {
    this.isDataAuditEnabled = !this.isDataAuditEnabled;
    if (typeof window !== 'undefined') {
      document.body.classList.toggle('reimagined-data-audit', this.isDataAuditEnabled);
      // Persist to localStorage for page refresh persistence
      localStorage.setItem('reimagined-data-audit', String(this.isDataAuditEnabled));
    }
  };

  /**
   * Open the data source documentation panel, optionally scrolling to a section
   */
  openDocs = (sectionId?: string) => {
    this.docsSectionId = sectionId;
    this.isDocsOpen = true;
  };

  /**
   * Close the data source documentation panel
   */
  closeDocs = () => {
    this.isDocsOpen = false;
    this.docsSectionId = undefined;
  };

  /**
   * Controls whether data source badges (Live Data, Demo Data, Not Connected)
   * are shown on reimagined UI components.
   *
   * Badge visibility is controlled by the Data Audit toggle (warning icon) in the header.
   * Can also be controlled via URL param ?show_badges=true/false.
   *
   * - In demo mode: badges always hidden
   * - In live mode + data audit OFF: badges hidden
   * - In live mode + data audit ON: badges shown
   */
  get showDataSourceBadges() {
    if (typeof window === 'undefined') {
      return false;
    }

    // URL param overrides everything
    const params = new URLSearchParams(window.location.search);
    const badgeParam = params.get('show_badges');
    if (badgeParam === 'true') return true;
    if (badgeParam === 'false') return false;

    // In demo mode, never show badges (demos are for sales/marketing)
    if (this.isDemoMode) {
      return false;
    }

    // In live mode, show badges only when data audit is enabled (tracked property)
    return this.isDataAuditEnabled;
  }

  get isDemoMode() {
    if (typeof window === 'undefined') {
      return false;
    }

    const params = new URLSearchParams(window.location.search);
    return (
      params.get('demo') === 'true' || params.has('demo_client') || params.has('demo_scenario')
    );
  }

  async fetchDashboardData() {
    return this.careerPathwaysApi.fetchDashboardData();
  }

  async fetchCareerCapitalScore() {
    return this.careerPathwaysApi.fetchCareerCapitalScore();
  }

  async fetchFinancialSnapshot() {
    if (this.isDemoMode) {
      return this.careerPathwaysApi.fetchFinancialSnapshot();
    }

    const snapshot = await this.careerPathwaysApi.fetchFinancialSnapshot();
    const loansData = await this.fetchLoansData();

    if (!loansData.hasLinkedLoans) {
      return snapshot;
    }

    const totalBalance = loansData.loans.reduce((sum, loan) => sum + loan.balance, 0);
    const totalMonthlyPayment = loansData.loans.reduce((sum, loan) => sum + loan.monthlyPayment, 0);
    const weightedRate = loansData.loans.reduce(
      (sum, loan) => sum + loan.balance * loan.interestRate,
      0
    );
    const interestRate = totalBalance > 0 ? Number((weightedRate / totalBalance).toFixed(1)) : 0;

    const payoffDates = loansData.loans.map((loan) => loan.estimatedPayoff).filter((date) => date);
    const estimatedPayoffDate =
      payoffDates.length > 0
        ? (payoffDates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] ??
          snapshot.studentLoans.estimatedPayoffDate)
        : snapshot.studentLoans.estimatedPayoffDate;

    const uniqueServicers = Array.from(new Set(loansData.loans.map((loan) => loan.servicer)));
    const servicer =
      uniqueServicers.length === 1 ? (uniqueServicers[0] ?? 'Loan Servicer') : 'Multiple servicers';

    const studentLoans: StudentLoanSummary = {
      ...snapshot.studentLoans,
      totalBalance,
      monthlyPayment: totalMonthlyPayment,
      interestRate,
      estimatedPayoffDate,
      servicer,
      paymentsMade: snapshot.studentLoans.paymentsMade ?? 0,
    };

    return {
      ...snapshot,
      studentLoans,
    };
  }

  async fetchScoreHistory(months: number) {
    return this.careerPathwaysApi.fetchScoreHistory(months);
  }

  async fetchSection127Recommendation() {
    return this.careerPathwaysApi.fetchSection127Recommendation();
  }

  async fetchLoansData() {
    if (this.isDemoMode) {
      return {
        isDemoMode: true,
        loans: [] as LoanAccount[],
        hasLinkedLoans: false,
      };
    }

    const personId = this.sessionContext.person?.id;
    if (!personId) {
      return {
        isDemoMode: false,
        loans: [] as LoanAccount[],
        hasLinkedLoans: false,
      };
    }

    const person = await this.store.findRecord<PersonModel>('person', personId, {
      include: 'accounts.financial-institution,accounts.loans',
      reload: true,
    });

    const activeAccounts = person.activeAccounts || [];
    const accountReceivingContributionsId = person.accountReceivingContributionsId;

    const loans = activeAccounts
      .map((account: AccountModel): LoanAccount => {
        const activeLoans = account.activeLoans || [];
        const loanCount = activeLoans.length;

        const originalBalance = activeLoans.reduce(
          (sum, loan: LoanModel) => sum + (loan.originalAmount || 0),
          0
        );
        const balance = activeLoans.reduce(
          (sum, loan: LoanModel) => sum + (loan.currentBalance || 0),
          0
        );

        const monthlyPayment =
          account.loanMinMonthlyPayment || account.providerRegularMonthlyPayment || 0;

        const nextPaymentDate =
          activeLoans.find((loan) => loan.nextPaymentDate)?.nextPaymentDate ||
          account.providerAttemptAt ||
          new Date().toISOString();

        const interestRate = Number(account.loanInterestAverage || 0);

        const loanClass = activeLoans[0]?.loanClass?.toLowerCase() || '';
        const accountType = loanClass.includes('private')
          ? 'private'
          : loanClass.includes('refinance')
            ? 'refinanced'
            : 'federal';

        return {
          id: account.id ?? '',
          servicer: account.institutionName || account.observation?.provider || 'Loan Servicer',
          balance,
          originalBalance: originalBalance || balance,
          interestRate,
          monthlyPayment,
          nextPaymentDate,
          estimatedPayoff: nextPaymentDate,
          isReceivingEmployerHelp: account.id === accountReceivingContributionsId,
          loanCount,
          accountType,
        };
      })
      .filter((loan) => loan.loanCount > 0);

    return {
      isDemoMode: false,
      loans,
      hasLinkedLoans: loans.length > 0,
    };
  }

  /**
   * Fetches employer contribution data from the employee service
   * Returns live data if available, otherwise falls back to demo data
   */
  async fetchEmployerContributions(): Promise<{
    contributions: EmployerContributions;
    isLiveData: boolean;
  }> {
    if (this.isDemoMode) {
      const snapshot = await this.careerPathwaysApi.fetchFinancialSnapshot();
      return {
        contributions: snapshot.employerContributions,
        isLiveData: false,
      };
    }

    // Wait for the employee contribution data to load
    const summary = this.employee.contributionSummary;

    if (!summary || summary.total === undefined) {
      // No live data available, fall back to demo
      const snapshot = await this.careerPathwaysApi.fetchFinancialSnapshot();
      return {
        contributions: snapshot.employerContributions,
        isLiveData: false,
      };
    }

    // Convert from cents to dollars
    const lifetimeTotal = (summary.total || 0) / 100;

    // Calculate year-to-date from contribution payments
    const currentYear = new Date().getFullYear();
    const ytdPayments = this.employee.contributionPayments.filter((payment) => {
      const paymentYear = new Date(
        payment.line_item_date || payment.line_item_period
      ).getFullYear();
      return paymentYear === currentYear;
    });

    const yearToDate =
      ytdPayments.reduce((total, payment) => total + (payment.amount || 0), 0) / 100;

    // Get the plan annual limit to estimate projected annual
    const plan = this.sessionContext.currentEmployee?.plan;
    const annualLimit = plan?.annualLimit || 5250;

    // Project based on current YTD and time elapsed in year
    const monthsElapsed = new Date().getMonth() + 1;
    const projectedAnnual = Math.min(Math.round((yearToDate / monthsElapsed) * 12), annualLimit);

    return {
      contributions: {
        yearToDate,
        lifetime: lifetimeTotal,
        projectedAnnual,
      },
      isLiveData: true,
    };
  }

  /**
   * Determines comprehensive data source info for all reimagined pages.
   * This tracks which data comes from live backend vs demo/placeholder sources.
   */
  async getDataSourceInfo(): Promise<DataSourceInfo> {
    const loansData = await this.fetchLoansData();
    const contributionsData = await this.fetchEmployerContributions();
    const section127Info = await this.fetchSection127Info();
    // Employee profile is available but not used directly in this method
    // It's used in fetchDashboardDataWithLive instead

    const hasEmployee = !!this.sessionContext.currentEmployee?.id;
    const hasPslfProfile = !!this.sessionContext.currentEmployee?.pslfProfile;
    const hasTasParticipant = !!this.sessionContext.currentEmployee?.tasParticipant;

    return {
      // Employee/Profile Data
      employeeProfile: hasEmployee ? 'live' : 'demo',
      employerName: this.sessionContext.company?.name ? 'live' : 'demo',

      // Financial Data
      studentLoans: loansData.hasLinkedLoans ? 'live' : 'demo',
      employerContributions: contributionsData.isLiveData ? 'live' : 'demo',
      section127Allocation: section127Info.isLiveData ? 'live' : 'demo',
      section127Used: section127Info.isLiveData ? 'live' : 'demo',

      // Benefit Eligibility
      pslfEligibility: hasPslfProfile || this.partner.isPslfEnabled ? 'live' : 'demo',
      tasEligibility: hasTasParticipant || this.partner.hasNewTuitionAssistance ? 'live' : 'demo',

      // Career Data - all placeholder until backend services built
      // GAP: Needs career-pathways microservice or API endpoints in tio-api
      careerCapitalScore: 'placeholder',
      skills: 'placeholder',
      careerPlan: 'placeholder',
      recommendations: 'placeholder',

      // Optimizer - placeholder until AI service built
      // GAP: Needs AI recommendation engine to analyze optimal allocation
      optimization: 'placeholder',
    };
  }

  /**
   * Fetches live employee profile data from session context.
   * This data is already loaded as part of the session.
   */
  fetchLiveEmployeeProfile(): LiveEmployeeProfile | null {
    if (this.isDemoMode) {
      return null;
    }

    const employee = this.sessionContext.currentEmployee;
    const person = this.sessionContext.person;
    const plan = employee?.plan;

    if (!employee?.id) {
      return null;
    }

    // Calculate years in role from employment start date
    const startDate = employee.employmentStartDate || employee.createdAt;
    const msPerYear = 1000 * 60 * 60 * 24 * 365;
    const yearsInRole = startDate
      ? Math.round(((Date.now() - new Date(startDate).getTime()) / msPerYear) * 10) / 10
      : 0;

    // Get contribution YTD from employee service
    const contributionPayments = this.employee.contributionPayments || [];
    const currentYear = new Date().getFullYear();
    const ytdPayments = contributionPayments.filter((payment) => {
      const paymentYear = new Date(
        payment.line_item_date || payment.line_item_period
      ).getFullYear();
      return paymentYear === currentYear;
    });
    const slrYtd = ytdPayments.reduce((total, payment) => total + (payment.amount || 0), 0) / 100;
    const slrLifetime = (this.employee.contributionSummary?.total || 0) / 100;

    return {
      id: employee.id,
      firstName: employee.firstName || person?.firstName || '',
      lastName: employee.lastName || person?.lastName || '',
      email: employee.email || person?.primaryEmail?.email || '',
      currentRole: {
        title: employee.title || 'Employee',
        department: employee.department || '',
        startDate: startDate || new Date().toISOString(),
        yearsInRole,
      },
      taBenefit: {
        annualMax: this.partner.hasNewTuitionAssistance ? 5250 : 0,
        used: 0, // GAP: Need to query TAS applications for used amount
        remaining: this.partner.hasNewTuitionAssistance ? 5250 : 0,
        eligiblePrograms: 0, // GAP: Need program catalog query
      },
      slrBenefit: {
        monthlyContribution: plan?.tiers?.[0]?.amount || 0,
        annualMax: plan?.annualLimit || 0,
        yearToDate: slrYtd,
        lifetime: slrLifetime,
      },
      pslfEligible: !!this.partner.isPslfEnabled,
      // GAP: PSLF payment count would come from pslf-form records or external API
      pslfPaymentCount: 0,
    };
  }

  /**
   * Fetches Section 127 allocation info.
   * Uses live data from plan/contributions when available.
   */
  async fetchSection127Info(): Promise<LiveSection127Info> {
    if (this.isDemoMode) {
      const snapshot = await this.careerPathwaysApi.fetchFinancialSnapshot();
      return {
        annualLimit: snapshot.section127.annualLimit,
        yearToDateUsed: snapshot.section127.yearToDateUsed,
        remaining: snapshot.section127.remaining,
        loanAllocation: snapshot.section127.loanAllocation,
        tuitionAllocation: snapshot.section127.tuitionAllocation,
        isLiveData: false,
      };
    }

    const plan = this.sessionContext.currentEmployee?.plan;
    const annualLimit = plan?.annualLimit || 5250;

    // Get YTD SLR contributions
    const contributionsData = await this.fetchEmployerContributions();
    const slrUsed = contributionsData.contributions.yearToDate;

    // Get YTD TAS usage from approved applications
    const tasUsedData = await this.fetchTasUsedAmount();
    const tasUsed = tasUsedData.yearToDate;

    const yearToDateUsed = slrUsed + tasUsed;
    const remaining = Math.max(0, annualLimit - yearToDateUsed);

    // Current allocation is based on what's been used
    const totalUsed = slrUsed + tasUsed || 1;
    const loanAllocation = slrUsed > 0 ? Math.round((slrUsed / totalUsed) * annualLimit) : 0;
    const tuitionAllocation = annualLimit - loanAllocation;

    const hasLiveData = contributionsData.isLiveData || tasUsedData.isLiveData;

    return {
      annualLimit,
      yearToDateUsed,
      remaining,
      loanAllocation: hasLiveData ? loanAllocation : 0,
      tuitionAllocation: hasLiveData ? tuitionAllocation : annualLimit,
      isLiveData: hasLiveData,
    };
  }

  /**
   * Fetches TAS (Tuition Assistance) used amount for current year.
   * Queries approved TAS applications and sums their pre-approved totals.
   *
   * @returns Object with yearToDate amount and whether data is live
   */
  async fetchTasUsedAmount(): Promise<{ yearToDate: number; isLiveData: boolean }> {
    if (this.isDemoMode) {
      return { yearToDate: 0, isLiveData: false };
    }

    const employee = this.sessionContext.currentEmployee;
    if (!employee?.id) {
      return { yearToDate: 0, isLiveData: false };
    }

    try {
      // Query TAS applications for this employee
      // Filter for approved states that count toward Section 127 usage
      const applications = await this.store.query<TasApplicationModel>('tas-application', {
        filter: {
          employee: employee.id,
        },
        include: 'tas-program-instance',
      });

      const currentYear = new Date().getFullYear();

      // Approved states that represent committed benefit usage
      const approvedStates = new Set([
        'TAS.ApplicationState.COURSES_APPROVED',
        'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
        'TAS.ApplicationState.EVIDENCE_APPROVED',
        'TAS.ApplicationState.FULFILLED',
      ]);

      // Sum pre-approved totals for current year applications in approved states
      const yearToDate = [...applications]
        .filter((app) => {
          // Check if application is in an approved state
          if (!approvedStates.has(app.state)) {
            return false;
          }

          // Check if application was created/approved in current year
          const appDate = new Date(app.createdAt);
          return appDate.getFullYear() === currentYear;
        })
        .reduce((sum, app) => {
          // preApprovedTotal is in cents, convert to dollars
          const approvedAmount = app.approvedTotal || 0;
          return sum + approvedAmount / 100;
        }, 0);

      return { yearToDate, isLiveData: true };
    } catch (error) {
      console.warn('Failed to fetch TAS used amount:', error);
      return { yearToDate: 0, isLiveData: false };
    }
  }

  /**
   * Fetches comprehensive dashboard data with live employee info where available.
   * Falls back to demo data for career-specific fields.
   */
  async fetchDashboardDataWithLive() {
    const demoData = await this.careerPathwaysApi.fetchDashboardData();
    const liveProfile = this.fetchLiveEmployeeProfile();

    if (!liveProfile || this.isDemoMode) {
      return {
        ...demoData,
        isLiveData: false,
      };
    }

    // Merge live employee data with demo career data
    return {
      ...demoData,
      employee: {
        ...demoData.employee,
        id: liveProfile.id,
        firstName: liveProfile.firstName,
        lastName: liveProfile.lastName,
        email: liveProfile.email,
        currentRole: liveProfile.currentRole,
        taBenefit: liveProfile.taBenefit,
      },
      isLiveData: true,
    };
  }

  /**
   * Fetches live PSLF data from pslf-profile and pslf-form models.
   * Returns enrollment status, qualifying payments, form history, and recent activity.
   */
  async fetchPslfData(): Promise<LivePslfData> {
    if (this.isDemoMode) {
      return this.getDemoPslfData();
    }

    const employee = this.sessionContext.currentEmployee;
    if (!employee?.id) {
      return this.getEmptyPslfData();
    }

    try {
      // Check if PSLF is enabled for the company
      const isPslfEnabled = this.partner.isPslfEnabled;
      if (!isPslfEnabled) {
        return this.getEmptyPslfData();
      }

      // Fetch PSLF profile with forms
      const pslfProfile = employee.pslfProfile as PslfProfileModel | null;

      if (!pslfProfile) {
        // User eligible but hasn't enrolled
        return {
          isEligible: true,
          isEnrolled: false,
          qualifyingPayments: 0,
          estimatedForgiveness: 0,
          forgivenessDate: null,
          currentEmployer: null,
          forms: [],
          recentActivity: [],
          isLiveData: true,
        };
      }

      // Fetch all PSLF forms for this profile
      const forms = await this.store.query<PslfFormModel>('pslf-form', {
        filter: {
          'pslf-profile': pslfProfile.id,
        },
        include: 'attachments',
      });

      // Process forms into our format (filter out any with null IDs)
      const processedForms: LivePslfForm[] = [...forms]
        .filter((form) => form.id !== null)
        .map((form) => ({
          id: form.id as string,
          status: form.status,
          formType: form.formType,
          employerName: form.employerDisplayName || 'Unknown Employer',
          employmentStartDate: form.borrowerFields?.['borrower-employment-start-date'] || null,
          employmentEndDate: form.borrowerFields?.['borrower-employment-end-date'] || null,
          borrowerSignedAt: form.borrowerCompletedAt || null,
          employerSignedAt: form.employerCompletedAt || null,
          submittedAt: form.submittedAt || null,
          createdAt: form.createdAt,
        }));

      // Calculate qualifying payments based on certified forms
      const certifiedForms = processedForms.filter(
        (f) => f.status === 'submitted' || f.status === 're_submitted'
      );

      // Estimate qualifying payments (this would come from MOHELA in production)
      // For now, estimate based on months of employment across submitted forms
      let totalMonths = 0;
      certifiedForms.forEach((form) => {
        if (form.employmentStartDate) {
          const start = new Date(form.employmentStartDate);
          const end = form.employmentEndDate ? new Date(form.employmentEndDate) : new Date();
          const months = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30));
          totalMonths += Math.max(0, months);
        }
      });

      const qualifyingPayments = Math.min(totalMonths, 120);

      // Get current employer from most recent form
      const currentForm = processedForms.find((f) => f.formType === 'currentEmployer');
      const currentEmployer = currentForm
        ? {
            name: currentForm.employerName,
            isQualifying: true, // Assume qualifying if they're filing ECF
            startDate: currentForm.employmentStartDate,
          }
        : null;

      // Build recent activity from forms
      const recentActivity = processedForms
        .filter((f) => f.submittedAt || f.borrowerSignedAt || f.employerSignedAt)
        .map((f) => {
          if (f.submittedAt) {
            return {
              type: 'form_submitted' as const,
              date: f.submittedAt,
              description: `ECF submitted for ${f.employerName}`,
            };
          }
          if (f.employerSignedAt) {
            return {
              type: 'form_signed' as const,
              date: f.employerSignedAt,
              description: `${f.employerName} signed your ECF`,
            };
          }
          return {
            type: 'form_signed' as const,
            date: f.borrowerSignedAt!,
            description: `You signed ECF for ${f.employerName}`,
          };
        })
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 5);

      // Estimate forgiveness amount from total loan balance
      const loansData = await this.fetchLoansData();
      const estimatedForgiveness = loansData.hasLinkedLoans
        ? loansData.loans.reduce((sum, loan) => sum + loan.balance, 0)
        : 0;

      // Calculate forgiveness date
      const paymentsRemaining = 120 - qualifyingPayments;
      const forgivenessDate =
        paymentsRemaining > 0
          ? new Date(Date.now() + paymentsRemaining * 30 * 24 * 60 * 60 * 1000).toISOString()
          : null;

      return {
        isEligible: true,
        isEnrolled: true,
        qualifyingPayments,
        estimatedForgiveness,
        forgivenessDate,
        currentEmployer,
        forms: processedForms,
        recentActivity,
        isLiveData: true,
      };
    } catch (error) {
      console.warn('Failed to fetch PSLF data:', error);
      return this.getEmptyPslfData();
    }
  }

  private getEmptyPslfData(): LivePslfData {
    return {
      isEligible: Boolean(this.partner.isPslfEnabled),
      isEnrolled: false,
      qualifyingPayments: 0,
      estimatedForgiveness: 0,
      forgivenessDate: null,
      currentEmployer: null,
      forms: [],
      recentActivity: [],
      isLiveData: true,
    };
  }

  private getDemoPslfData(): LivePslfData {
    return {
      isEligible: true,
      isEnrolled: true,
      qualifyingPayments: 84,
      estimatedForgiveness: 45230,
      forgivenessDate: '2029-03-15',
      currentEmployer: {
        name: 'Acme Healthcare',
        isQualifying: true,
        startDate: '2022-01-15',
      },
      forms: [
        {
          id: 'demo-1',
          status: 'submitted',
          formType: 'currentEmployer',
          employerName: 'Acme Healthcare',
          employmentStartDate: '2022-01-15',
          employmentEndDate: null,
          borrowerSignedAt: '2025-12-01',
          employerSignedAt: '2025-12-05',
          submittedAt: '2025-12-06',
          createdAt: '2025-11-28',
        },
        {
          id: 'demo-2',
          status: 'submitted',
          formType: 'priorEmployer',
          employerName: 'City General Hospital',
          employmentStartDate: '2019-06-01',
          employmentEndDate: '2021-12-31',
          borrowerSignedAt: '2024-01-10',
          employerSignedAt: '2024-01-15',
          submittedAt: '2024-01-16',
          createdAt: '2024-01-08',
        },
      ],
      recentActivity: [
        {
          type: 'form_submitted',
          date: '2025-12-06',
          description: 'ECF submitted for Acme Healthcare',
        },
        {
          type: 'form_signed',
          date: '2025-12-05',
          description: 'Acme Healthcare signed your ECF',
        },
        {
          type: 'payment_certified',
          date: '2025-11-15',
          description: '12 payments certified (72→84 total)',
        },
      ],
      isLiveData: false,
    };
  }

  /**
   * Fetches live TAS data including eligibility, programs, and applications.
   */
  async fetchTasData(): Promise<LiveTasData> {
    if (this.isDemoMode) {
      return this.getDemoTasData();
    }

    const employee = this.sessionContext.currentEmployee;
    if (!employee?.id) {
      return this.getEmptyTasData();
    }

    try {
      // Check if TAS is enabled
      const hasTas = this.partner.hasNewTuitionAssistance;
      if (!hasTas) {
        return this.getEmptyTasData();
      }

      // Get TAS participant
      const participant = employee.tasParticipant;
      if (!participant) {
        return {
          isEligible: true,
          participantState: null,
          programs: [],
          applications: [],
          summary: {
            totalApproved: 0,
            totalPaid: 0,
            pendingCount: 0,
            activeCount: 0,
            completedCount: 0,
          },
          isLiveData: true,
        };
      }

      // Fetch TAS applications with related data
      const applications = await this.store.query<TasApplicationModel>('tas-application', {
        filter: {
          employee: employee.id,
        },
        include: 'tas-program-instance.tas-program-template,tas-courses',
      });

      // Process applications (filter out any with null IDs)
      const processedApps: LiveTasApplication[] = [...applications]
        .filter((app) => app.id !== null)
        .map((app) => ({
          id: app.id as string,
          name: app.displayName || 'Application',
          state: app.state,
          paymentState: app.paymentState || 'TAS.ApplicationPaymentState.NONE',
          approvedTotal: (app.approvedTotal || 0) / 100, // Convert from cents
          paidTotal: (app.amounts?.paid || 0) / 100,
          coursesBeginDate: app.coursesBeginDate || null,
          coursesEndDate: app.fields?.COURSES_END_DATE?.values?.[0] || null,
          createdAt: app.createdAt,
          programName: app.tasProgramInstance?.tasProgramTemplate?.programName || null,
        }));

      // Get unique programs
      const programInstances = await this.store.query<TasProgramInstanceModel>(
        'tas-program-instance',
        {
          filter: {
            employee: employee.id,
          },
          include: 'tas-program-template',
        }
      );

      const processedPrograms: LiveTasProgram[] = [...programInstances]
        .filter((pi) => pi.id !== null)
        .map((pi) => ({
          id: pi.id as string,
          name: pi.tasProgramTemplate?.programName || 'Program',
          state: pi.state,
          institution: pi.fields?.SCHOOL_INSTITUTION_NAME?.values?.[0] || null,
          major: pi.fields?.PROGRAM_MAJOR?.values?.[0] || null,
          startDate: pi.fields?.ESTIMATED_PROGRAM_BEGIN?.values?.[0] || null,
        }));

      // Calculate summary
      const approvedStates = new Set([
        'TAS.ApplicationState.COURSES_APPROVED',
        'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
        'TAS.ApplicationState.EVIDENCE_APPROVED',
        'TAS.ApplicationState.FULFILLED',
      ]);

      const pendingStates = new Set([
        'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
        'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
      ]);

      const activeStates = new Set([
        'TAS.ApplicationState.ATTEND',
        'TAS.ApplicationState.COURSES_APPROVED',
      ]);

      const summary = {
        totalApproved: processedApps
          .filter((app) => approvedStates.has(app.state))
          .reduce((sum, app) => sum + app.approvedTotal, 0),
        totalPaid: processedApps.reduce((sum, app) => sum + app.paidTotal, 0),
        pendingCount: processedApps.filter((app) => pendingStates.has(app.state)).length,
        activeCount: processedApps.filter((app) => activeStates.has(app.state)).length,
        completedCount: processedApps.filter(
          (app) => app.state === 'TAS.ApplicationState.FULFILLED'
        ).length,
      };

      return {
        isEligible: true,
        participantState: participant.state,
        programs: processedPrograms,
        applications: processedApps,
        summary,
        isLiveData: true,
      };
    } catch (error) {
      console.warn('Failed to fetch TAS data:', error);
      return this.getEmptyTasData();
    }
  }

  private getEmptyTasData(): LiveTasData {
    return {
      isEligible: this.partner.hasNewTuitionAssistance,
      participantState: null,
      programs: [],
      applications: [],
      summary: {
        totalApproved: 0,
        totalPaid: 0,
        pendingCount: 0,
        activeCount: 0,
        completedCount: 0,
      },
      isLiveData: true,
    };
  }

  private getDemoTasData(): LiveTasData {
    const scenario = getCurrentDemoTasScenarioId();

    const baseProgram: LiveTasProgram = {
      id: 'demo-prog-1',
      name: 'Data Analytics Certificate',
      state: 'TAS.ProgramInstanceState.IN_PROGRESS',
      institution: 'Coursera / Google',
      major: 'Data Analytics',
      startDate: '2025-09-01',
    };

    const baseApplication: LiveTasApplication = {
      id: 'demo-app-1',
      name: 'Fall 2025 - Data Analytics',
      state: 'TAS.ApplicationState.ATTEND',
      paymentState: 'TAS.ApplicationPaymentState.PAID_IN_PART',
      approvedTotal: 1500,
      paidTotal: 750,
      coursesBeginDate: '2025-09-01',
      coursesEndDate: '2025-12-15',
      createdAt: '2025-08-15',
      programName: 'Data Analytics Certificate',
    };

    const scenarioData: Record<
      ReturnType<typeof getCurrentDemoTasScenarioId>,
      { programs: LiveTasProgram[]; applications: LiveTasApplication[] }
    > = {
      'not-started': {
        programs: [],
        applications: [],
      },
      'in-progress': {
        programs: [baseProgram],
        applications: [
          baseApplication,
          {
            id: 'demo-app-2',
            name: 'AWS Certification',
            state: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
            paymentState: 'TAS.ApplicationPaymentState.NONE',
            approvedTotal: 0,
            paidTotal: 0,
            coursesBeginDate: '2026-02-01',
            coursesEndDate: '2026-04-30',
            createdAt: '2026-01-20',
            programName: 'Professional Certifications',
          },
        ],
      },
      approved: {
        programs: [baseProgram],
        applications: [
          {
            ...baseApplication,
            state: 'TAS.ApplicationState.COURSES_APPROVED',
            paymentState: 'TAS.ApplicationPaymentState.NONE',
            approvedTotal: 2400,
            paidTotal: 0,
          },
        ],
      },
      rejected: {
        programs: [baseProgram],
        applications: [
          {
            ...baseApplication,
            state: 'TAS.ApplicationState.COURSES_REJECTED',
            paymentState: 'TAS.ApplicationPaymentState.NONE',
            approvedTotal: 0,
            paidTotal: 0,
          },
        ],
      },
      completed: {
        programs: [
          {
            ...baseProgram,
            state: 'TAS.ProgramInstanceState.COMPLETED',
          },
        ],
        applications: [
          {
            ...baseApplication,
            state: 'TAS.ApplicationState.FULFILLED',
            paymentState: 'TAS.ApplicationPaymentState.PAID',
            approvedTotal: 3000,
            paidTotal: 3000,
          },
        ],
      },
    };

    const { programs, applications } = scenarioData[scenario];

    const summary = {
      totalApproved: applications.reduce((sum, app) => sum + app.approvedTotal, 0),
      totalPaid: applications.reduce((sum, app) => sum + app.paidTotal, 0),
      pendingCount: applications.filter((app) =>
        [
          'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
          'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
        ].includes(app.state)
      ).length,
      activeCount: applications.filter((app) =>
        ['TAS.ApplicationState.ATTEND', 'TAS.ApplicationState.COURSES_APPROVED'].includes(app.state)
      ).length,
      completedCount: applications.filter((app) => app.state === 'TAS.ApplicationState.FULFILLED')
        .length,
    };

    return {
      isEligible: true,
      participantState: 'TAS.ParticipantState.ACTIVE',
      programs,
      applications,
      summary,
      isLiveData: false,
    };
  }

  /**
   * Fetches comprehensive benefits summary combining SLR, TAS, and PSLF
   */
  async fetchBenefitsSummary() {
    const [contributionsData, tasData, pslfData, section127Info] = await Promise.all([
      this.fetchEmployerContributions(),
      this.fetchTasData(),
      this.fetchPslfData(),
      this.fetchSection127Info(),
    ]);

    return {
      section127: section127Info,
      slr: {
        enrolled: contributionsData.isLiveData && contributionsData.contributions.yearToDate > 0,
        yearToDate: contributionsData.contributions.yearToDate,
        lifetime: contributionsData.contributions.lifetime,
        isLiveData: contributionsData.isLiveData,
      },
      tas: {
        enrolled: tasData.applications.length > 0,
        ...tasData.summary,
        isLiveData: tasData.isLiveData,
      },
      pslf: {
        enrolled: pslfData.isEnrolled,
        qualifyingPayments: pslfData.qualifyingPayments,
        estimatedForgiveness: pslfData.estimatedForgiveness,
        isLiveData: pslfData.isLiveData,
      },
      hasAnyBenefits:
        contributionsData.contributions.yearToDate > 0 ||
        tasData.applications.length > 0 ||
        pslfData.isEnrolled,
    };
  }

  async fetchLrapDiscovery() {
    return this.careerPathwaysApi.fetchLrapDiscovery();
  }

  async fetchLrapPrograms() {
    return this.careerPathwaysApi.fetchLrapPrograms();
  }

  async fetchCareerFamilies() {
    return this.careerPathwaysApi.fetchCareerFamilies();
  }

  async fetchFeaturedRoles() {
    return this.careerPathwaysApi.fetchFeaturedRoles();
  }

  async fetchCareerRole(roleId: string) {
    return this.careerPathwaysApi.fetchCareerRole(roleId);
  }

  async fetchRolePathways(roleId: string) {
    return this.careerPathwaysApi.fetchRolePathways(roleId);
  }

  async fetchAllRolesForComparison() {
    return this.careerPathwaysApi.fetchAllRolesForComparison();
  }

  async fetchAssessmentQuestions(skillId: string) {
    return this.careerPathwaysApi.fetchAssessmentQuestions(skillId);
  }

  async fetchEmployeeSkills() {
    return this.careerPathwaysApi.fetchEmployeeSkills();
  }

  async fetchSkillGaps() {
    return this.careerPathwaysApi.fetchSkillGaps();
  }

  async fetchCareerPlan() {
    return this.careerPathwaysApi.fetchCareerPlan();
  }

  async fetchAvailableGoals() {
    return this.careerPathwaysApi.fetchAvailableGoals();
  }

  async fetchRecommendations() {
    return this.careerPathwaysApi.fetchRecommendations();
  }

  async fetchSuccessStories() {
    return this.careerPathwaysApi.fetchSuccessStories();
  }

  async fetchPslfVault() {
    return this.careerPathwaysApi.fetchPslfVault();
  }
}
